'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MessageCircle } from 'lucide-react';

interface TravelGuideCTAProps {
  countryName: string;
  relatedJourneys: Array<{
    id: number;
    title: Record<string, string>;
    subtitle: Record<string, string>;
    image: string;
    duration: string;
    groupSize: string;
    startingPrice: string;
    currency: string;
  }>;
  locale: string;
}

export default function TravelGuideCTA({ countryName, relatedJourneys, locale }: TravelGuideCTAProps) {
  const { t } = useTranslation();

  return (
    <section className="py-8 bg-gradient-to-br from-primary-50/50 via-white to-neutral-cream/30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Compact Helper Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <MessageCircle className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-gray-900 mb-1.5">
                    {t('travelGuides.cta.compact.heading', { country: countryName })}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {t('travelGuides.cta.compact.description', { country: countryName })}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 md:flex-shrink-0">
              <Link
                href={`/${locale}/contact`}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                {t('travelGuides.cta.compact.contactUs')}
              </Link>
              {relatedJourneys.length > 0 && (
                <Link
                  href={`/${locale}/signature-journeys`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  {t('travelGuides.cta.compact.viewSignatureJourneys')}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>

          {/* Optional: Show related journeys as compact chips */}
          {relatedJourneys.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">
                {t('travelGuides.cta.compact.featuredJourneys', { country: countryName })}
              </p>
              <div className="flex flex-wrap gap-2">
                {relatedJourneys.slice(0, 3).map((journey) => {
                  const title = journey.title[locale as keyof typeof journey.title] || journey.title.en;
                  
                  return (
                    <Link
                      key={journey.id}
                      href={`/${locale}/signature-journeys/${journey.id}`}
                      className="group inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 rounded-full hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    >
                      <span className="line-clamp-1">{title}</span>
                      <span className="text-[10px] text-gray-500 group-hover:text-primary-600">
                        {journey.duration}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

